<template>
  <section>
    <section v-if="!professional">
      <div class="p-5" style="height: 100vh">
        <loader/>
      </div>
    </section>
    <section v-else>
      <div class="py-4 px-3 p-lg-4" v-if="professional.status === 1">
        <div v-if="canBookProfessional(professional)">
          <div class="row row-professional-detail">
            <!-- Share and Fav buttons -->
            <div class="col-12 text-right">
              <div class="section-share-button">
                <div class="btn btn-light btn-sm" @click="openShareProfileModal()">
                  <i class="fal fa-share-square nav-icon"></i>&nbsp;{{ $t('message.professional.menu.share') }}
                </div>
                <template v-if="!professional.is_favorited_professional">
                  <div class="btn btn-light btn-sm" @click="updateFavProfessional(true)">
                    <i class="fal fa-heart nav-icon"></i>&nbsp;{{ $t('message.professional.menu.save') }}
                  </div>
                </template>
                <template v-else>
                  <div class="btn btn-light btn-sm" @click="updateFavProfessional(false)">
                    <i class="fas fa-heart nav-icon text-danger"></i>&nbsp;{{ $t('message.professional.menu.saved') }}
                  </div>
                </template>
              </div>
            </div>
            <!-- Cleaner photos -->
            <div class="col-12 pt-3 pb-4">
              <cleaner-photos :professional="professional"/>
            </div>
            <!-- Cleaner detail -->
            <div class="col-md-7" id="professional-content">
              <cleaner-profile-desktop :professional="professional" v-if="!$mq.phone"/>
              <cleaner-profile-mobile :professional="professional" :isActiveReviewLink="true" v-if="$mq.phone"/>
              <cleaner-description-section :professional="professional"/>
              <hr class="mb-0">
              <div class="pt-4">
                <cleaner-reviews-section />
              </div>
            </div>
            <!-- /.col-md-7 -->
            <div class="col-md-5" v-if="professional.status === 1 && $mq.desktop">
              <div class="card">
                <div class="card-body pb-3 pt-0 px-0">
                  <book-cleaner-form
                    :professional="professional"
                    :from="'cleanerDetail'"
                    class="form-book-cleaner"/>
                </div>
              </div>
            </div>
            <!-- /.col-md-5 -->
            <!-- Cleaner location -->
            <div class="col-12 pt-3">
              <cleaner-map :professional="professional"/>
            </div>
            <!-- Cleaner card -->
            <div class="col-12 pt-3 pb-5 pb-lg-2 mb-2" v-if="professional.professional_services.findIndex(item => { item.service_id === 1 }) > -1">
              <img src="/static/img/professional_card_back.jpg" alt="" class="img-fluid rounded-all">
            </div>
          </div>
        </div>
        <div class="p-5" v-else>
          <h3 class="text-center">
            <i class="fas fa-exclamation-triangle -status-pending"></i>
            {{ $t('message.professional.service_not_matched') }}
          </h3>
          <div class="pt-3" v-if="professional.experiences.length > 0">
            <hr>
            <div class="text-center py-3">
              <h4>
                {{ $t('message.experience.other_services', { name: professional.firstname }) }}
              </h4>
            </div>
            <div class="row">
              <div class="col-lg-4 col-sm-6 col-12" v-for="(experience, index) in professional.experiences"
                   :key="index">
                <experience :experience="experience" :displayCost="false" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="p-5" v-else>
        <h3 class="text-center">
          <i class="fas fa-exclamation-triangle -status-pending"></i>
          {{ $t('message.professional.not_available') }}
        </h3>
      </div>
      <!-- Modals -->
      <book-cleaner-modal :professional="professional" v-if="professional.status === 1 && canBookProfessional(professional) && !$mq.desktop"/>
      <share-profile-modal ref="shareProfileModal"/>
    </section>
  </section>
</template>

<script>
  import { mapGetters } from 'vuex'
  import BookCleanerForm from './components/BookCleanerForm'
  import BookCleanerModal from './components/BookCleanerModal'
  import CleanerPhotos from './components/CleanerPhotos'
  import CleanerProfileDesktop from './components/CleanerProfileDesktop'
  import CleanerProfileMobile from './components/CleanerProfileMobile'
  import CleanerDescriptionSection from './components/CleanerDescriptionSection'
  import CleanerReviewsSection from './components/CleanerReviewsSection'
  import CleanerMap from './components/CleanerMap'
  import Experience from '../experience/Experience'
  import ShareProfileModal from './components/ShareProfileModal'
  import Loader from '../loader/Loader'
  import userEventLogHelpers from '../../helpers/userEventLogHelpers'
  
  export default {
    components: {
      BookCleanerForm,
      BookCleanerModal,
      CleanerPhotos,
      CleanerProfileDesktop,
      CleanerProfileMobile,
      CleanerDescriptionSection,
      CleanerReviewsSection,
      CleanerMap,
      Experience,
      ShareProfileModal,
      Loader
    },
    data () {
      return {
        userId: 0,
        professionalId: this.$route.params.id,
        pageTitle: 'untitled',
        canonicalHref: null,
        meta: []
      }
    },
    metaInfo () {
      return {
        title: this.pageTitle,
        htmlAttrs: {
          lang: this.$i18n.locale
        },
        meta: this.meta,
        link: [
          {rel: 'canonical', href: this.canonicalHref}
        ]
      }
    },
    computed: {
      ...mapGetters({
        isLoadMore: 'professionalReviews/isLoadMore',
        globalState: 'globalVal/getState',
        professional: 'professional/getProfessional',
        state: 'professionalReviews/getState',
        stateBooking: 'booking/getState',
        user: 'auth/getUser'
      })
    },
    watch: {
      professional () {
        if (this.professional) {
          this.canonicalHref = `https://beneat.co/cleaner/${this.professional.id}`
          this.pageTitle = this.$options.filters.firstName(this.professional.name) + ' | '
          if (this.professional.firstname_th) this.pageTitle += this.professional.firstname_th + ' | '
          this.pageTitle += this.$i18n.t('message.professional.cleaning_expert_in')
          this.pageTitle += this.$options.filters.multiLangName(this.globalState.cities[this.professional.province_id])
          this.pageTitle += ' | BeNeat.co'

          this.meta = [
            {charset: 'utf-8'},
            {name: 'viewport', content: 'width=device-width, initial-scale=1'},
            {vmid: 'description', name: 'description', content: this.professional.description},
            {name: 'robots', content: 'index,follow'},
            {property: 'og:title', content: this.pageTitle},
            {property: 'og:site_name', content: 'BeNeat จองบริการแม่บ้านออนไลน์ แม่บ้านรายวัน แม่บ้านรายชั่วโมง แม่บ้านรายครั้ง ได้สะดวก รวดเร็ว พร้อมรับประกันคุณภาพ'},
            {property: 'og:description', content: this.professional.description},
            {property: 'og:type', content: 'profile'},
            {property: 'og:url', content: `https://beneat.co/cleaner/${this.professional.id}`},
            {property: 'og:image', content: this.professional.picture}
          ]
        }
      },
      user () {
        this.initialData()
      }
    },
    methods: {
      initialData () {
        if (this.professional) {
          this.shareUrl = 'https://beneat.co/cleaner/' + this.professionalId
          this.shareText = 'Highly recommend! ' + this.professional.name + ' @beneat'
        }
        this.userId = (this.user) ? this.user.id : 0
        const queryData = {
          user_id: this.userId,
          professional_id: this.professionalId
        }
        this.$store.dispatch('professional/LOAD_PROFESSIONAL_DETAIL', queryData)
      },
      openShareProfileModal () {
        this.$refs.shareProfileModal.openModal(this.professional)
      },
      canBookProfessional (professional) {
        if (professional && professional.service_ids.length > 0) {
          const serviceIds = [1, 2, 16, 39]
          for (const serviceId of professional.service_ids) {
            return (serviceIds.indexOf(serviceId) !== -1)
          }
        }
        return false
      },
      updateFavProfessional (status) {
        if (this.user) {
          const submitData = {
            user_id: this.user.id,
            professional_id: this.professional.id
          }
          if (status) {
            this.$store.dispatch('user/ADD_FAVORITE_PROFESSIONAL', submitData)
          } else {
            this.$store.dispatch('user/DELETE_FAVORITE_PROFESSIONAL', submitData)
          }
          this.$store.commit('professional/UPDATE_FAVORITE_PROFESSIONAL', status)
        } else {
          this.$parent.openModal('login')
        }
      }
    },
    mounted () {
      this.initialData()
      userEventLogHelpers.logEvent('view_professional', {
        professional_id: this.professionalId
      })
    },
    created () {
      this.$store.commit('professional/SET_PROFESSIONAL_DETAIL', null)
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/professional/CleanerDetail.vue